import * as React from 'react';
import {Prompt} from 'react-router-dom';

import type {History} from 'history';

import {Nut_Capabilities} from 'nutshell-core/settings-constants';

import {Button} from 'shells/button/button';
import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {PageControlBar} from 'shells/control-bar/page-control-bar';
import {ImportStatusBanner} from 'shells/dromo/import-status-banner';
import {Stack} from 'shells/layout/stack/stack';
import {LoadingPage} from 'shells/loading-page/loading-page';

import {
    ImportIdentifier,
    type GetYearOverviewStats_quotaYearOverview as QuotaYearOverview,
    type ImportStatus,
    type UserQuotaFragment,
} from 'nutshell-graphql-types';

import {addNotification} from '../../../notifications/index';

import {useGetSetting} from '../../core/use-get-setting';
import {BannerCueCards} from '../../glossary/layout/banner-cue-cards';
import {CueCardStack} from '../../glossary/layout/cue-card-stack';
import {useGetCueCardsForLocation} from '../../glossary/layout/helpers';
import {PageContainer, PageTitleBar} from '../../layout';
import {useGetDromoImports} from '../../setup/import/dromo/graphql/hooks/use-get-dromo-imports';
import {DromoQuotaImportButton} from '../../setup/import/dromo/quota/quota-dromo-import-button';

import {AddQuotaModal} from './add-quota-modal';
import {useCreateQuota} from './graphql/hooks/use-create-quota';
import {useDeleteQuota} from './graphql/hooks/use-delete-quota';
import {useDuplicateQuota} from './graphql/hooks/use-duplicate-quota';
import {useExportQuotas} from './graphql/hooks/use-export-quotas';
import {useGetUsersWithQuotas} from './graphql/hooks/use-get-users-with-quotas';
import {useGetYearOverviewStats} from './graphql/hooks/use-get-year-overview-stats';
import {useUpdateQuota} from './graphql/hooks/use-update-quota';
import {getYears, getYearTabs, sortUsersByQuotaExistenceAndName, type MonthValues} from './helpers';
import {SalesQuotasEmptyState} from './sales-quotas-empty-state';
import {UserQuotaCard} from './user-quota-card';
import {YearSummaryTable} from './year-summary-table';

import styles from './sales-quotas-page.module.css';

const yearTabs = getYearTabs();

interface Props {
    activeTab: number;
    history: History;
}

export function SalesQuotasPage(props: Props) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [nextLocation, setNextLocation] = React.useState<string | null>(null);
    const [draftUser, setDraftUser] = React.useState<UserQuotaFragment | null>(null);
    const [quotasBeingEdited, setQuotasBeingEdited] = React.useState<Record<string, boolean>>({});
    const [quotaImportBannerStatus, setQuotaImportBannerStatus] =
        React.useState<ImportStatus | null>(null);

    const [scrollToUserId, setScrollToUserId] = React.useState<string | null>(null);
    const [openCardIds, setOpenCardIds] = React.useState<Set<string>>(new Set());
    const quotaCardRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

    const {refetch: refetchImports} = useGetDromoImports(ImportIdentifier.QUOTA);
    const {users, isLoading, refetch: refetchUsers} = useGetUsersWithQuotas();

    const {createQuota} = useCreateQuota();
    const {duplicateQuota} = useDuplicateQuota();
    const {updateQuota} = useUpdateQuota();
    const {deleteQuota} = useDeleteQuota();
    const {exportQuotas} = useExportQuotas();

    const cueCards = useGetCueCardsForLocation('/sales/sales-quotas', 'settings-sidebar');

    const {setting: wasMigratedToQuotasV2} = useGetSetting({
        settingName: Nut_Capabilities.SETTING_HAS_MIGRATED_TO_QUOTAS_V2,
    } as const);

    const fiscalYear = Number(props.activeTab);

    const {
        quotaYearOverview,
        refetch: refetchYearOverview,
        isLoading: isYearOverviewLoading,
        isRefetching: isYearOverviewRefetching,
    } = useGetYearOverviewStats(getYears());

    const activeTabYearStats: QuotaYearOverview | undefined = quotaYearOverview?.find(
        (overview) => overview.fiscalYear === fiscalYear
    );

    const handleEditStarts = (userId: string, fiscalYear: number) => {
        const key = getKey(userId, fiscalYear);
        const isDirty = false;
        setQuotasBeingEdited((prev) => ({...prev, [key]: isDirty}));
    };

    const handleEditStops = (userId: string, fiscalYear: number) => {
        const key = getKey(userId, fiscalYear);
        setQuotasBeingEdited((prev) => {
            const newState = {...prev};
            delete newState[key];
            return newState;
        });
    };

    const handleEditChange = (userId: string, fiscalYear: number, isDirty: boolean) => {
        const key = getKey(userId, fiscalYear);
        setQuotasBeingEdited((prev) => ({...prev, [key]: isDirty}));
    };

    const handleClearEditState = () => {
        setDraftUser(null);
        setQuotasBeingEdited({});
    };

    const handleCreateQuota = (user: UserQuotaFragment, monthValues: MonthValues) => {
        setOpenCardIds((prev) => new Set(prev).add(user.id));

        createQuota(
            {
                userId: user.id,
                monthValues: Object.entries(monthValues).map(([month, value]) => ({
                    month: Number(month),
                    value: Number(value),
                })),
                fiscalYear,
            },
            user
        )
            .then(() => {
                setScrollToUserId(user.id);
            })
            .catch(() => {
                setOpenCardIds((prev) => {
                    const next = new Set(prev);
                    next.delete(user.id);
                    return next;
                });

                addNotification('Something went wrong', {
                    icon: 'frowny',
                    body: 'We encountered a problem while creating the quota. Please try again.',
                });
            });
    };

    const handleUpdateQuota = (user: UserQuotaFragment, monthValues: MonthValues) => {
        updateQuota({
            userId: user.id,
            fiscalYear,
            months: Object.entries(monthValues).map(([month, value]) => ({
                identifier: Number(month),
                value: Number(value),
            })),
        }).catch(() => {
            addNotification('Something went wrong', {
                icon: 'frowny',
                body: 'We encountered a problem while updating the quota. Please try again.',
            });
        });
    };

    const handleDeleteQuota = (userId: string) => {
        return deleteQuota({userId, fiscalYear});
    };

    const handleDuplicateQuota = async (userId: string, duplicateFromUserId: string) => {
        setOpenCardIds((prev) => new Set(prev).add(userId));

        return duplicateQuota({
            userIds: [userId],
            duplicateFromUserId,
        }).then(() => {
            setScrollToUserId(userId);
            setIsModalOpen(false);
        });
    };

    const handleDuplicateFromModalStart = (userIds: string[]) => {
        setOpenCardIds((prev) => {
            const next = new Set(prev);
            userIds.forEach((id) => next.add(id));
            return next;
        });
    };

    const handleCardOpened = (userId: string) => {
        setOpenCardIds((prev) => {
            const next = new Set(prev);
            next.delete(userId);
            return next;
        });
    };

    const importRefresh = () => {
        refetchImports().then((result) => {
            const imports = result.data?.dromoImports;
            if (
                imports?.recentCompletedImport?.importStatus === 'COMPLETED' &&
                !imports?.currentImport
            ) {
                setQuotaImportBannerStatus('COMPLETED');
            }
        });
        refetchUsers();
        refetchYearOverview();
    };

    React.useEffect(() => {
        if (scrollToUserId) {
            const user = users?.find((u) => u.id === scrollToUserId);
            const hasQuotaForCurrentYear = user?.quotas.some(
                (quota) => quota.fiscalYear === fiscalYear
            );

            if (hasQuotaForCurrentYear) {
                const cardElement = quotaCardRefs.current[scrollToUserId];
                if (cardElement) {
                    cardElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }
            }
            setScrollToUserId(null);
        }
    }, [scrollToUserId, users, fiscalYear]);

    return (
        <>
            <PageContainer
                title='Sales quotas'
                whiteBackground={true}
                pageTitleBar={
                    <PageTitleBar
                        title='Sales quotas'
                        subheaderPrimaryText='Distribute your sales quotas throughout the year and amongst your team members.'
                        buttons={
                            <>
                                <Button
                                    variant='secondary'
                                    size='big'
                                    onClick={() => {
                                        exportQuotas().then(({data}) => {
                                            if (data?.quotasExport) {
                                                window.open(data.quotasExport.url, '_blank');
                                            }
                                        });
                                    }}
                                >
                                    Export
                                </Button>
                                <DromoQuotaImportButton
                                    variant='secondary'
                                    onUploaded={() => {
                                        setQuotaImportBannerStatus('IN_PROGRESS');
                                        // wait a few seconds, these imports usually complete pretty quick
                                        setTimeout(() => {
                                            importRefresh();
                                        }, 5000);
                                    }}
                                />
                                <Button
                                    variant='primary'
                                    size='big'
                                    onClick={() => setIsModalOpen(true)}
                                >
                                    Add a quota
                                </Button>
                            </>
                        }
                    />
                }
                controlBar={
                    <PageControlBar
                        tabs={{
                            options: yearTabs,
                            selectedTab: String(props.activeTab),
                            onChangeTab: (tab: string) => {
                                props.history.push(`/sales/sales-quotas?fiscalYear=${tab}`);
                            },
                        }}
                    />
                }
            >
                {wasMigratedToQuotasV2?.value === '1' && <BannerCueCards />}
                <div className={styles.container}>
                    <div className='overflow-auto full-height full-width border-box flex flex-dir-col gap-16'>
                        {quotaImportBannerStatus !== null && (
                            <ImportStatusBanner
                                onRefresh={importRefresh}
                                onViewEntity={() => {
                                    setQuotaImportBannerStatus(null);
                                }}
                                status={quotaImportBannerStatus}
                                type='quotas'
                                completedButtonText={'Dismiss'}
                            />
                        )}
                        {!users || (users.length === 0 && !draftUser) || !activeTabYearStats ? (
                            <Stack justifyContent='center' alignItems='center' isFullHeight={true}>
                                {isLoading || isYearOverviewLoading ? (
                                    <LoadingPage />
                                ) : (
                                    <SalesQuotasEmptyState />
                                )}
                            </Stack>
                        ) : (
                            <Stack spacing={16}>
                                {draftUser && (
                                    <UserQuotaCard
                                        key={draftUser.id}
                                        user={draftUser}
                                        fiscalYear={fiscalYear}
                                        isEditing={true}
                                        onCancelEdit={() => {
                                            setDraftUser(null);
                                            handleEditStops(draftUser.id, fiscalYear);
                                        }}
                                        onEditChange={(isDirty) =>
                                            handleEditChange(draftUser.id, fiscalYear, isDirty)
                                        }
                                        onSave={(monthValues) => {
                                            setDraftUser(null);
                                            handleEditStops(draftUser.id, fiscalYear);
                                            handleCreateQuota(draftUser, monthValues);
                                        }}
                                    />
                                )}
                                {users
                                    .sort((user1, user2) =>
                                        sortUsersByQuotaExistenceAndName(user1, user2, fiscalYear)
                                    )
                                    .map((user) => (
                                        <UserQuotaCard
                                            key={user.id}
                                            setScrollToUserId={setScrollToUserId}
                                            getRef={(ref) => {
                                                quotaCardRefs.current[user.id] = ref;
                                            }}
                                            user={user}
                                            fiscalYear={fiscalYear}
                                            shouldStartOpen={openCardIds.has(user.id)}
                                            isEditing={isBeingEdited(
                                                quotasBeingEdited,
                                                user.id,
                                                fiscalYear
                                            )}
                                            onEdit={() => handleEditStarts(user.id, fiscalYear)}
                                            onCancelEdit={() =>
                                                handleEditStops(user.id, fiscalYear)
                                            }
                                            onEditChange={(isDirty) =>
                                                handleEditChange(user.id, fiscalYear, isDirty)
                                            }
                                            onSave={(monthValues, isNewQuota) => {
                                                handleEditStops(user.id, fiscalYear);

                                                if (isNewQuota) {
                                                    handleCreateQuota(user, monthValues);
                                                } else {
                                                    handleUpdateQuota(user, monthValues);
                                                }
                                            }}
                                            onDelete={() => handleDeleteQuota(user.id)}
                                            onDuplicateStart={handleDuplicateFromModalStart}
                                            onCardOpened={handleCardOpened}
                                            existingUsers={users}
                                        />
                                    ))}
                            </Stack>
                        )}
                    </div>
                    <div className={styles['sidebar-container']}>
                        {activeTabYearStats && (
                            <YearSummaryTable
                                quotaYearOverview={activeTabYearStats}
                                showEmptyState={users.length === 0 && !draftUser}
                                isRefetching={isYearOverviewRefetching}
                            />
                        )}
                        <div className='mt-16'>
                            <CueCardStack cards={cueCards || []} />
                        </div>
                    </div>
                </div>
                {isModalOpen && (
                    <AddQuotaModal
                        onClose={() => setIsModalOpen(false)}
                        onAddDraftUser={(user) => {
                            setDraftUser(user);
                            handleEditStarts(user.id, fiscalYear);
                        }}
                        onDuplicateQuota={handleDuplicateQuota}
                        existingUsers={users}
                        selectedFiscalYear={fiscalYear}
                    />
                )}
            </PageContainer>
            <Prompt
                when={true}
                message={(location) => {
                    // If a user clicks on the current tab, we don't need to prompt them
                    if (location.search === props.history.location.search) {
                        return true;
                    }

                    if (Object.values(quotasBeingEdited).some((isDirty) => isDirty)) {
                        const fullLocation = `${location.pathname}${location.search}`;
                        if (fullLocation === nextLocation) {
                            setNextLocation(null);
                            return true;
                        }

                        setNextLocation(fullLocation);
                        return false;
                    }

                    // A user might have started editing without ever dirtying the state
                    // If so, we still need to clear the state so no cards are stuck in edit mode
                    handleClearEditState();
                    return true;
                }}
            />
            {nextLocation && (
                <ConfirmationDialog
                    title='Are you sure you want to leave?'
                    message='You have unsaved changes that will be lost if you leave this page.'
                    confirmText='Leave'
                    cancelText='Stay'
                    onConfirm={() => {
                        handleClearEditState();
                        props.history.push(nextLocation);
                    }}
                    onCancel={() => {
                        setNextLocation(null);
                    }}
                />
            )}
        </>
    );
}

const getKey = (userId: string, fiscalYear: number) => `${userId}-${fiscalYear}`;
const isBeingEdited = (
    quotasBeingEdited: Record<string, boolean>,
    userId: string,
    fiscalYear: number
): boolean => {
    const key = getKey(userId, fiscalYear);
    const keys = Object.keys(quotasBeingEdited);
    return keys.some((k) => k === key);
};
